import React, { useState, useEffect } from 'react';

// Use the PHP backend URL directly
const API_BASE_URL = 'http://spreadsheets.isilverstar.in/api';

const AdminSpreadsheet = ({ worksheet, cells, onSave }) => {
  // Convert cells array to a map for easier access
  const [cellsMap, setCellsMap] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [fileInputKey, setFileInputKey] = useState(0); // Used to reset file input
  const [insertRowPosition, setInsertRowPosition] = useState(null); // Track where to insert row
  const [insertColPosition, setInsertColPosition] = useState(null); // Track where to insert column
  const [showFileUpload, setShowFileUpload] = useState(null); // Track which cell should show file upload

  // Initialize cellsMap from props
  useEffect(() => {
    const map = {};
    cells.forEach(cell => {
      const key = `${cell.row_index}-${cell.col_index}`;
      map[key] = { ...cell };
    });
    setCellsMap(map);
  }, [cells]);

  // Determine grid dimensions
  const maxRow = Math.max(...Object.values(cellsMap).map(cell => cell.row_index), 0);
  const maxCol = Math.max(...Object.values(cellsMap).map(cell => cell.col_index), 0);
  const rows = Math.max(maxRow + 10, 20); // Minimum 20 rows, extend by 10
  const cols = Math.max(maxCol + 5, 10); // Minimum 10 columns, extend by 5

  // Generate column headers (A, B, C, ...)
  const getColumnLabel = (index) => {
    let label = '';
    while (index >= 0) {
      label = String.fromCharCode(65 + (index % 26)) + label;
      index = Math.floor(index / 26) - 1;
    }
    return label;
  };

  const handleCellClick = (rowIndex, colIndex) => {
    const key = `${rowIndex}-${colIndex}`;
    const cell = cellsMap[key];
    
    // If we're already editing this cell, don't do anything
    if (editingCell && editingCell.rowIndex === rowIndex && editingCell.colIndex === colIndex) {
      return;
    }
    
    setEditingCell({ rowIndex, colIndex });
    setEditValue(cell ? (cell.content || '') : '');
  };

  const handleEditChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleEditSave = () => {
    if (!editingCell) return;

    const { rowIndex, colIndex } = editingCell;
    const key = `${rowIndex}-${colIndex}`;
    
    // Update the cell in the map
    const updatedCellsMap = {
      ...cellsMap,
      [key]: {
        ...cellsMap[key],
        row_index: rowIndex,
        col_index: colIndex,
        content: editValue,
        // Clear attachment if we're editing text content
        ...(cellsMap[key] && !editValue && { 
          attachment_url: null, 
          attachment_filename: null 
        })
      }
    };
    
    setCellsMap(updatedCellsMap);
    setEditingCell(null);
    setEditValue('');
    setShowFileUpload(null);
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
    setShowFileUpload(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleEditSave();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  const handleAddRow = (position) => {
    // Set the position where we want to insert a new row
    setInsertRowPosition(position);
  };

  const handleAddColumn = (position) => {
    // Set the position where we want to insert a new column
    setInsertColPosition(position);
  };

  const insertRow = (position) => {
    // Shift all rows at or after the position down by 1
    const updatedCellsMap = {};
    
    Object.values(cellsMap).forEach(cell => {
      if (cell.row_index >= position) {
        // Move this cell down by 1 row
        const newKey = `${cell.row_index + 1}-${cell.col_index}`;
        updatedCellsMap[newKey] = {
          ...cell,
          row_index: cell.row_index + 1
        };
      } else {
        // Keep this cell in its current position
        const key = `${cell.row_index}-${cell.col_index}`;
        updatedCellsMap[key] = cell;
      }
    });
    
    setCellsMap(updatedCellsMap);
    setInsertRowPosition(null);
  };

  const insertColumn = (position) => {
    // Shift all columns at or after the position right by 1
    const updatedCellsMap = {};
    
    Object.values(cellsMap).forEach(cell => {
      if (cell.col_index >= position) {
        // Move this cell right by 1 column
        const newKey = `${cell.row_index}-${cell.col_index + 1}`;
        updatedCellsMap[newKey] = {
          ...cell,
          col_index: cell.col_index + 1
        };
      } else {
        // Keep this cell in its current position
        const key = `${cell.row_index}-${cell.col_index}`;
        updatedCellsMap[key] = cell;
      }
    });
    
    setCellsMap(updatedCellsMap);
    setInsertColPosition(null);
  };

  const handleFileUploadClick = (rowIndex, colIndex, e) => {
    e.stopPropagation();
    setShowFileUpload({ rowIndex, colIndex });
  };

  const handleFileUpload = async (rowIndex, colIndex, file) => {
    console.log('handleFileUpload called with:', { rowIndex, colIndex, file });
    
    if (!file) {
      console.log('No file provided');
      return;
    }

    try {
      console.log('Uploading file:', file);
      console.log('File name:', file.name);
      console.log('File size:', file.size);
      console.log('File type:', file.type);

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf', 
                           'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                           'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                           'text/plain'];
      
      if (!allowedTypes.includes(file.type)) {
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt'];
        
        if (!allowedExtensions.includes(fileExtension)) {
          throw new Error(`File type not allowed. Allowed types: ${allowedExtensions.join(', ')}`);
        }
      }

      const formData = new FormData();
      formData.append('file', file);

      // Add a timeout to the fetch request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      console.log('Upload response status:', response.status);
      console.log('Upload response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      console.log('Upload successful:', data);
      
      const key = `${rowIndex}-${colIndex}`;
      
      // Update the cell with attachment info
      const updatedCellsMap = {
        ...cellsMap,
        [key]: {
          ...cellsMap[key],
          row_index: rowIndex,
          col_index: colIndex,
          content: '', // Clear text content when adding attachment
          attachment_url: data.url,
          attachment_filename: data.filename
        }
      };
      
      setCellsMap(updatedCellsMap);
      // Reset file input
      setFileInputKey(prev => prev + 1);
      setShowFileUpload(null);
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.name === 'AbortError') {
        alert('File upload timed out. Please try again with a smaller file.');
      } else {
        alert('Failed to upload file: ' + error.message);
      }
      setShowFileUpload(null);
    }
  };

  const handleSaveAll = () => {
    // Convert cellsMap to array format for saving
    const cellsArray = Object.values(cellsMap).map(cell => ({
      rowIndex: cell.row_index,
      colIndex: cell.col_index,
      content: cell.content || '',
      attachmentUrl: cell.attachment_url || null,
      attachmentFilename: cell.attachment_filename || null
    }));
    
    onSave(cellsArray);
  };

  const handleClearCell = (rowIndex, colIndex) => {
    const key = `${rowIndex}-${colIndex}`;
    
    // Remove the cell from the map
    const updatedCellsMap = { ...cellsMap };
    delete updatedCellsMap[key];
    
    setCellsMap(updatedCellsMap);
  };

  return (
    <div className="admin-spreadsheet">
      <div className="mb-4 flex space-x-2">
        <button
          onClick={handleSaveAll}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Save All Changes
        </button>
        <div className="relative group">
          <button
            onClick={() => handleAddRow(0)}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
          >
            Add Row
          </button>
          {insertRowPosition !== null && (
            <div className="absolute left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10">
              <div className="py-1">
                <button
                  onClick={() => insertRow(insertRowPosition)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Insert at position {insertRowPosition + 1}
                </button>
                <button
                  onClick={() => setInsertRowPosition(null)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="relative group">
          <button
            onClick={() => handleAddColumn(0)}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
          >
            Add Column
          </button>
          {insertColPosition !== null && (
            <div className="absolute left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10">
              <div className="py-1">
                <button
                  onClick={() => insertColumn(insertColPosition)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Insert at column {getColumnLabel(insertColPosition)}
                </button>
                <button
                  onClick={() => setInsertColPosition(null)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="spreadsheet-container overflow-auto border border-gray-300 rounded-lg shadow-sm">
        <table className="spreadsheet-table border-collapse">
          <thead>
            <tr>
              <th className="cell header-cell border border-gray-300 bg-gray-100 w-12 h-8"></th>
              {Array.from({ length: cols + 1 }).map((_, colIndex) => (
                <th 
                  key={colIndex} 
                  className="cell header-cell border border-gray-300 bg-gray-100 w-32 h-8 font-medium relative"
                >
                  <div className="flex items-center justify-between">
                    <span>{getColumnLabel(colIndex)}</span>
                    {colIndex < cols && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddColumn(colIndex);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows + 1 }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                <td className="cell header-cell border border-gray-300 bg-gray-100 w-12 h-8 font-medium relative">
                  <div className="flex items-center justify-between">
                    <span>{rowIndex + 1}</span>
                    {rowIndex < rows && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddRow(rowIndex);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
                {Array.from({ length: cols + 1 }).map((_, colIndex) => {
                  const key = `${rowIndex}-${colIndex}`;
                  const cell = cellsMap[key];
                  const isEditing = editingCell && 
                    editingCell.rowIndex === rowIndex && 
                    editingCell.colIndex === colIndex;
                  const showUpload = showFileUpload &&
                    showFileUpload.rowIndex === rowIndex &&
                    showFileUpload.colIndex === colIndex;
                  
                  return (
                    <td 
                      key={colIndex} 
                      className="cell border border-gray-300 w-32 h-8 p-1 text-sm relative"
                    >
                      {isEditing ? (
                        <div className="flex flex-col h-full">
                          <input
                            type="text"
                            value={editValue}
                            onChange={handleEditChange}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            className="w-full h-6 border border-blue-500 rounded px-1"
                          />
                          <div className="flex space-x-1 mt-1">
                            <button 
                              onClick={handleEditSave}
                              className="text-xs bg-green-500 text-white px-1 rounded"
                            >
                              Save
                            </button>
                            <button 
                              onClick={handleEditCancel}
                              className="text-xs bg-red-500 text-white px-1 rounded"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="w-full h-full cursor-pointer"
                          onClick={() => handleCellClick(rowIndex, colIndex)}
                        >
                          {cell ? (
                            <>
                              {cell.attachment_url ? (
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <a 
                                      href={`http://spreadsheets.isilverstar.in${cell.attachment_url}`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" />
                                      </svg>
                                    </a>
                                    <span className="ml-1 truncate text-xs">{cell.attachment_filename}</span>
                                  </div>
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClearCell(rowIndex, colIndex);
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <div className="truncate">{cell.content}</div>
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClearCell(rowIndex, colIndex);
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-gray-400 text-xs">
                              Click to edit
                            </div>
                          )}
                          
                          {/* File upload button for this cell */}
                          <button
                            onClick={(e) => handleFileUploadClick(rowIndex, colIndex, e)}
                            className="absolute bottom-0 right-0 text-gray-500 hover:text-gray-700 p-1"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                          
                          {/* File upload input - only shown when explicitly requested */}
                          {showUpload && (
                            <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center">
                              <input
                                key={fileInputKey}
                                type="file"
                                ref={(input) => {
                                  // Add a ref to the input element for debugging
                                  if (input) {
                                    input.addEventListener('change', (e) => {
                                      console.log('File input change event triggered via ref');
                                      console.log('Files selected:', e.target.files);
                                      if (e.target.files && e.target.files.length > 0) {
                                        handleFileUpload(rowIndex, colIndex, e.target.files[0]);
                                      }
                                    });
                                  }
                                }}
                                onChange={(e) => {
                                  console.log('File input onChange triggered');
                                  console.log('Files selected:', e.target.files);
                                  console.log('Event object:', e);
                                  if (e.target.files && e.target.files.length > 0) {
                                    handleFileUpload(rowIndex, colIndex, e.target.files[0]);
                                  } else {
                                    console.log('No files selected');
                                  }
                                }}
                                onClick={(e) => {
                                  console.log('File input clicked');
                                  e.stopPropagation();
                                }}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              />
                              <div className="text-gray-700 text-sm">
                                Select file to upload
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminSpreadsheet;